import type { FieldDecision } from '@app/src/types/tabularImport'
import { isReadOnlyField, type FieldConfig } from './fieldConfig'

export type FieldStatus = 'same' | 'import_empty' | 'existing_empty' | 'differs'

export function getFieldStatus(importVal: string | null, existingVal: string | null): FieldStatus {
  const imp = importVal?.trim() || null
  const ex = existingVal?.trim() || null

  if (imp === ex) return 'same'
  if (!imp) return 'import_empty'
  if (!ex) return 'existing_empty'
  return 'differs'
}

export function buildInitialDecisions(
  configs: FieldConfig[],
  extractedFields: Record<string, string | null>,
  snapshot: Record<string, string | null>,
  savedDecisions: Record<string, FieldDecision> | null,
): Record<string, FieldDecision> {
  const allKeys = new Set([...Object.keys(extractedFields), ...Object.keys(snapshot)])
  const decisions: Record<string, FieldDecision> = {}

  for (const key of allKeys) {
    if (isReadOnlyField(configs, key)) {
      decisions[key] = 'keep'
      continue
    }
    if (savedDecisions?.[key]) {
      decisions[key] = savedDecisions[key]
      continue
    }
    const status = getFieldStatus(extractedFields[key] ?? null, snapshot[key] ?? null)

    if (status === 'import_empty' || status === 'same') {
      decisions[key] = 'keep'
    } else if (key === 'Owners') {
      // Owners is multi-value — default to union rather than replace so re-imports
      // don't silently drop existing owners.
      decisions[key] = 'add'
    } else if (status === 'existing_empty') {
      decisions[key] = 'import'
    } else {
      decisions[key] = 'keep'
    }
  }

  return decisions
}

export function applyBulkDecision(
  configs: FieldConfig[],
  allKeys: string[],
  extractedFields: Record<string, string | null>,
  snapshot: Record<string, string | null>,
  decisions: Record<string, FieldDecision>,
  target: FieldDecision,
): Record<string, FieldDecision> {
  const next: Record<string, FieldDecision> = { ...decisions }
  for (const key of allKeys) {
    if (isReadOnlyField(configs, key)) continue
    const status = getFieldStatus(extractedFields[key] ?? null, snapshot[key] ?? null)
    if (status === 'differs') next[key] = target
  }
  return next
}
